import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, take, tap } from 'rxjs/operators';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { QaiFieldComponent, QaiInputComponent } from '@qai/ui';
import { DetailsSectionComponent } from '../../../components/details-container/details-section.component';
import { AvatarLookVoiceSelectorComponent, AvatarSelection } from '../../../components/avatar-look-voice-selector/avatar-look-voice-selector.component';
import { AuthorService, SIMULATION_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/author.service';
import { AuthorReactiveFormService } from '../../../services/author-reactive-form.service';
import { SelfAuthoringPersonaFormService } from '../../../services/self-authoring-persona-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { IGeneratedCallDetails } from 'src/app/interfaces/self-serve/responses/igenerated-call-details';
import { getLocalStorageRoleNames } from 'src/app/utils/local-storage.util';

// General tab "Simulation information" for Knowledge Check (GUIDED) calls: simulation name,
// avatar appearance and avatar name. The name saves to the simulation; the avatar fields
// save to the persona (createPersona/updatePersona in echo mode).
@Component({
  selector: 'knowledge-check-simulation-information',
  standalone: true,
  templateUrl: './knowledge-check-simulation-information.component.html',
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    QaiFieldComponent,
    QaiInputComponent,
    DetailsSectionComponent,
    AvatarLookVoiceSelectorComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class KnowledgeCheckSimulationInformationComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorService = inject(AuthorService);
  private readonly authorReactiveFormService = inject(AuthorReactiveFormService);
  private readonly personaFormService = inject(SelfAuthoringPersonaFormService);

  callUuid = '';
  moduleUuid = '';
  personaForm?: FormGroup;
  simulationNameControl?: FormControl;
  /** Transloco key of the inline name error, empty when there is none. */
  simulationNameErrorKey = '';
  /** Published Knowledge Checks can't be edited (the backend rejects simulation/persona updates). */
  readOnly = false;
  /** Simulation overview (getSimulationOverview). Only its skillUuid is synced into the form; it is not pushed into the persona form. */
  generatedDetails = signal<IGeneratedCallDetails | null>(null);

  private generalForm?: FormGroup;
  private lastSavedSimulationName = '';
  private isRevertingSimulationName = false;
  private isQcAdmin = false;

  /** Non-QC Admins can only rename a draft; QC Admins can rename in any status. Read on every
   * check because the status can change while the page is open (e.g. test assignments sent). */
  get isSimulationNameReadOnly(): boolean {
    const status = this.authorReactiveFormService.getSimulationStatus(this.callUuid);
    return (status || '').toUpperCase() !== 'DRAFT' && !this.isQcAdmin;
  }

  get avatarNameControl(): FormControl | undefined {
    return this.personaForm?.get('personaName') as FormControl | undefined;
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.moduleUuid = this.route.snapshot.queryParamMap.get('moduleUuid') || '';
    if (!this.callUuid) return;

    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.isQcAdmin = getLocalStorageRoleNames().includes('QCAdmin');
    this.personaForm = this.personaFormService.getOrCreateForm(this.callUuid);
    this.listenToAvatarNameChanges();
    this.fetchGeneratedCallDetails();

    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.generalForm = form.get(SimulationConstants.GENERAL_SECTION) as FormGroup;
        this.simulationNameControl = this.generalForm.get('simulationName') as FormControl;
        this.lastSavedSimulationName = (this.simulationNameControl.value || '').trim();
        this.listenToSimulationNameChanges();
      });
  }

  onAvatarSelected(selection: AvatarSelection | undefined): void {
    if (this.readOnly) return;
    this.personaFormService.updateAvatarSelection(this.callUuid, selection?.replicaId, selection?.gender);
  }

  onSavedAvatarResolved(selection: AvatarSelection): void {
    this.personaFormService.setReplicaGender(this.callUuid, selection.gender);
  }

  private listenToSimulationNameChanges(): void {
    this.simulationNameControl?.valueChanges
      .pipe(
        filter(() => !this.isRevertingSimulationName && !this.isSimulationNameReadOnly),
        tap(() => (this.simulationNameErrorKey = '')),
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged(),
        map((name: string) => (name || '').trim()),
        filter((name) => name !== this.lastSavedSimulationName),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((name) => {
        if (!name) {
          // Keep the field empty so the user can type a new name; nothing is sent to the backend.
          this.simulationNameErrorKey = 'simulationBuilder.NAME_REQUIRED_ERROR';
          return;
        }
        if (name.length > SimulationConstants.SIMULATION_NAME_MAX_LENGTH) {
          this.simulationNameErrorKey = 'simulationBuilder.NAME_MAX_LENGTH_ERROR';
          return;
        }
        this.saveSimulationName(name);
      });
  }

  private fetchGeneratedCallDetails(): void {
    let rubricEditedDuringFetch = false;
    let rubricEditSubscription: Subscription | undefined;
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        rubricEditSubscription = form
          .get(`${SimulationConstants.SKILL_PROMPT_PATH}.rubric`)
          ?.valueChanges.pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe(() => (rubricEditedDuringFetch = true));
      });

    this.authorService
      .getGeneratedCallDetails(this.callUuid)
      .pipe(
        finalize(() => rubricEditSubscription?.unsubscribe()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (details) => {
          this.generatedDetails.set(details);
          if (!rubricEditedDuringFetch) {
            this.prefillSkillRubric(details.skillUuid);
          }
        },
        error: (err) => console.error('Failed to fetch generated call details:', err),
      });
  }

  /** Prefills the Scoring section's skills rubric dropdown from the overview. Only fills an empty
   * rubric so a selection already on the form is kept, and patches silently because the skill is
   * already linked on the backend (no updateSkillSelection call needed). Skipped by the caller if
   * the rubric was edited while the overview request was in flight. */
  private prefillSkillRubric(skillUuid: string | null): void {
    if (!skillUuid) return;

    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        const currentRubric = form.get(`${SimulationConstants.SKILL_PROMPT_PATH}.rubric`)?.value;
        if (currentRubric) return;

        this.authorReactiveFormService.patchFormSilently(this.callUuid, SimulationConstants.SKILL_PROMPT_PATH, {
          rubric: skillUuid,
        });
      });
  }

  private saveSimulationName(name: string): void {
    this.authorReactiveFormService.updateFormUpdatedAt(this.callUuid);
    this.authorService
      .updateSimulation({
        uuid: this.callUuid,
        existingModuleUuid: this.generalForm?.value.moduleUuid || '',
        name,
      })
      .subscribe({
        next: () => (this.lastSavedSimulationName = name),
        error: (err) => {
          console.error('Failed to update simulation name:', err);
          if (err?.status !== SIMULATION_NAME_ALREADY_EXISTS_STATUS) return;
          // A newer edit is already on its way, so don't overwrite it with the old name.
          if ((this.simulationNameControl?.value || '').trim() !== name) return;

          this.simulationNameErrorKey = 'simulationBuilder.DUPLICATE_NAME_ERROR_SIMULATION';
          this.revertSimulationName();
        },
      });
  }

  /** Restores the last saved name. Emits so the header breadcrumb follows, but skips the autosave. */
  private revertSimulationName(): void {
    this.isRevertingSimulationName = true;
    this.simulationNameControl?.setValue(this.lastSavedSimulationName);
    this.isRevertingSimulationName = false;
  }

  private listenToAvatarNameChanges(): void {
    this.avatarNameControl?.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.personaFormService.saveIfComplete(this.callUuid));
  }
}
